import * as R from 'ramda';
import * as Yup from 'yup';
// features
import { ROOT_BRANCH_GUID } from '../../branch/constants';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import { makeGroupedStatesOptionsByCountries } from '../../../helpers/options';
//////////////////////////////////////////////////

const inputWrapperStyles = { mb: 25, mr: 10, width: 220 };

export const validationSchemaGeoFencingZoneObject = {
  [GC.FIELD_GEO_FENCING_ZONE_NAME]: Yup.string().nullable(true).required(G.getRequiredLocaleTxt()),
  [GC.FIELD_GEO_FENCING_ZONE_ZONE_ADDRESS_POINTS]: Yup.array().of(Yup.object().shape({
    [GC.FIELD_GEO_FENCING_ZONE_COUNTRIES]: G.yupArrayRequired,
    [GC.FIELD_GEO_FENCING_ZONE_STATES]: Yup.array().nullable(true),
    [GC.FIELD_GEO_FENCING_ZONE_CITIES]: Yup.array().nullable(true),
    [GC.FIELD_GEO_FENCING_ZONE_ZIP_CODES]: Yup.array().nullable(true),
    [GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_TO]: Yup.string().nullable(true),
    [GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_FROM]: Yup.string().nullable(true),
    [GC.FIELD_ZIP_PREFIX]: G.yupStringNotRequired.max(6, G.getShouldBeFromToCharLocaleTxt(0, 6)),
  })),
};

export const defaultZoneAddressPointsFields = () => ({
  id: G.genShortId(),
  [GC.FIELD_GEO_FENCING_ZONE_CITIES]: [],
  [GC.FIELD_GEO_FENCING_ZONE_STATES]: [],
  [GC.FIELD_GEO_FENCING_ZONE_ZIP_CODES]: [],
  [GC.FIELD_GEO_FENCING_ZONE_COUNTRIES]: null,
  [GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_TO]: null,
  [GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_FROM]: null,
});

export const defaultGeoFencingZoneFields = {
  [GC.FIELD_GEO_FENCING_ZONE_NAME]: null,
  [GC.FIELD_GEO_FENCING_ZONE_ZONE_ADDRESS_POINTS]: R.of(Array, defaultZoneAddressPointsFields()),
};

export const isZoneUsageAvailable = (type) => R.equals(type, 'report');

export const ZONE_USAGE_OPERATIONS = 'OPERATIONS';
export const ZONE_USAGE_OPERATIONS_AND_ANALYTICS = 'OPERATIONS_AND_ANALYTICS';
export const ZONE_USAGE_ANALYTICS_ONLY = 'ANALYTICS_ONLY';

export const getZoneUsageOptions = () => [
  {
    value: ZONE_USAGE_OPERATIONS,
    label: G.getWindowLocale('titles:operations-only', 'Operations Only'),
  },
  {
    value: ZONE_USAGE_OPERATIONS_AND_ANALYTICS,
    label: G.getWindowLocale('titles:operations-and-analytics', 'Operations and Analytics'),
  },
  {
    value: ZONE_USAGE_ANALYTICS_ONLY,
    label: G.getWindowLocale('titles:analytics-only', 'Analytics Only'),
  },
];

export const getZoneUsageLabel = (zoneUsage) => R.propOr(
  zoneUsage,
  GC.FIELD_LABEL,
  R.find(({ value }) => R.equals(value, zoneUsage), getZoneUsageOptions()),
);

export const getDefaultGeoFencingZoneFields = (type) => G.ifElse(
  isZoneUsageAvailable(type),
  R.assoc(GC.FIELD_GEO_FENCING_ZONE_ZONE_USAGE, ZONE_USAGE_OPERATIONS, defaultGeoFencingZoneFields),
  defaultGeoFencingZoneFields,
);

export const geoFencingZoneFieldName = {
  type: 'text',
  isRequired: true,
  label: ['titles:name', 'Name'],
  fieldName: GC.FIELD_GEO_FENCING_ZONE_NAME,
  inputWrapperStyles: { mb: 10, width: 220 },
};

// a zone being created belongs to the current branch, an edited one to its own enterprise
export const geoFencingZoneUsageDisabledReason = (initialValues) => G.ifElse(
  R.equals(
    R.propOr(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, R.or(initialValues, {})),
    ROOT_BRANCH_GUID,
  ),
  G.getWindowLocale(
    'messages:zone-usage-not-on-root',
    'Zones of the root enterprise cannot be used in analytics.',
  ),
  null,
);

// where each option applies, and how the analytics sync stamps orders with a region — on the next
// sync, not on save
const geoFencingZoneUsageInfo = () => G.getWindowLocale(
  'messages:zone-usage-info',
  'Operations: dispatch boards, available drivers and report filters. Analytics: a region in the ' +
  'analytics reports. An order uses the analytics zones of its division, or of the nearest parent ' +
  'enterprise that has any. Orders are matched by country, state and zip — cities are ignored, the ' +
  'most specific match wins, and where zones overlap the newest zone wins. Locations matching no ' +
  'region are reported as UNKNOWN. Orders already synced are re-matched on the next sync, not on save.',
);

export const geoFencingZoneUsageField = (initialValues) => {
  const disabledReason = geoFencingZoneUsageDisabledReason(initialValues);

  return {
    type: 'reactSelect',
    isClearable: false,
    options: getZoneUsageOptions(),
    // the standard info icon next to the label — why the select is locked, or what each option does
    info: R.or(disabledReason, geoFencingZoneUsageInfo()),
    disabled: G.isNotNilAndNotEmpty(disabledReason),
    fieldName: GC.FIELD_GEO_FENCING_ZONE_ZONE_USAGE,
    inputWrapperStyles: { mb: 10, ml: 10, width: 220 },
    label: ['titles:zone-usage', 'Zone Usage'],
  };
};

export const geoFencingZoneColorField = (type) => ({
  options: 'colors',
  type: 'reactSelect',
  fieldName: GC.FIELD_COLOR,
  label: ['titles:color', 'Color'],
  inputWrapperStyles: { mb: 10, ml: 10, width: 180, display: G.ifElse(R.equals(type, 'report'), 'flex', 'none') },
});

export const geoFencingZoneMainFields = (type, initialValues) => {
  const fields = [geoFencingZoneFieldName, geoFencingZoneColorField(type)];

  return G.ifElse(
    isZoneUsageAvailable(type),
    R.append(geoFencingZoneUsageField(initialValues), fields),
    fields,
  );
};

const getAddressCommonFields = (index, type, countries) => [
  {
    inputWrapperStyles,
    useMenuPortalTarget: true,
    type: 'addressAutocomplete',
    label: ['titles:search-city', 'Search City'],
    placeholder: G.getWindowLocale('titles:search-location', 'Search Location'),
    closeMenuOnScroll: R.pathEq('zone_address_points_wrapper', ['target', 'id']),
    fieldName: `${GC.FIELD_GEO_FENCING_ZONE_ZONE_ADDRESS_POINTS}.${index}.searchCity`,
  },
  {
    isMulti: true,
    isGrouped: true,
    inputWrapperStyles,
    type: 'reactSelect',
    useMenuPortalTarget: true,
    label: ['titles:states', 'States'],
    options: makeGroupedStatesOptionsByCountries(countries || []),
    closeMenuOnScroll: R.pathEq('zone_address_points_wrapper', ['target', 'id']),
    fieldName: `${GC.FIELD_GEO_FENCING_ZONE_ZONE_ADDRESS_POINTS}.${index}.${GC.FIELD_GEO_FENCING_ZONE_STATES}`,
  },
  {
    isMulti: true,
    isRequired: true,
    type: 'reactSelect',
    options: 'countryOptions',
    useMenuPortalTarget: true,
    label: ['titles:countries', 'Countries'],
    closeMenuOnScroll: R.pathEq('zone_address_points_wrapper', ['target', 'id']),
    inputWrapperStyles: {
      ...inputWrapperStyles,
      mr: G.ifElse(R.equals(type, 'report'), 10, '0px'),
    },
    fieldName: `${GC.FIELD_GEO_FENCING_ZONE_ZONE_ADDRESS_POINTS}.${index}.${GC.FIELD_GEO_FENCING_ZONE_COUNTRIES}`,
  },
  {
    useMenuPortalTarget: true,
    type: 'addressAutocomplete',
    label: ['titles:search-zip', 'Search Zip'],
    placeholder: G.getWindowLocale('titles:search-location', 'Search Location'),
    inputWrapperStyles: {
      ...inputWrapperStyles,
      mr: G.ifElse(R.equals(type, 'report'), 0, inputWrapperStyles.mr),
    },
    fieldName: `${GC.FIELD_GEO_FENCING_ZONE_ZONE_ADDRESS_POINTS}.${index}.searchZip`,
  },
  {
    type: 'multiItems',
    inputWrapperStyles,
    label: ['titles:zip-codes', 'Zip Codes'],
    placeholder: G.getWindowLocale('titles:enter-zip-and-press-enter', 'Enter zip and press Enter'),
    fieldName: `${GC.FIELD_GEO_FENCING_ZONE_ZONE_ADDRESS_POINTS}.${index}.${GC.FIELD_GEO_FENCING_ZONE_ZIP_CODES}`,
  },
  {
    inputWrapperStyles,
    label: ['titles:zip-from', 'Zip From'],
    fieldName: `${GC.FIELD_GEO_FENCING_ZONE_ZONE_ADDRESS_POINTS}.${index}.${GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_FROM}`,
  },
  {
    label: ['titles:zip-to', 'Zip To'],
    inputWrapperStyles: { mb: 25, width: 220 },
    fieldName: `${GC.FIELD_GEO_FENCING_ZONE_ZONE_ADDRESS_POINTS}.${index}.${GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_TO}`,
  },
];

export const zoneAddressPointsFields = (index, type, countries) => {
  const fields = getAddressCommonFields(index, type, countries);

  if (R.equals(type, 'report')) {
    return R.insert(4, {
      maxLength: 6,
      inputWrapperStyles,
      label: ['titles:zip-prefix', 'Zip Prefix'],
      fieldName: `${GC.FIELD_GEO_FENCING_ZONE_ZONE_ADDRESS_POINTS}.${index}.${GC.FIELD_ZIP_PREFIX}`,
    }, fields);
  }

  return R.insert(1, {
    type: 'multiItems',
    inputWrapperStyles,
    label: ['titles:cities', 'Cities'],
    placeholder: G.getWindowLocale('titles:enter-city-and-press-enter', 'Enter city and press Enter'),
    fieldName: `${GC.FIELD_GEO_FENCING_ZONE_ZONE_ADDRESS_POINTS}.${index}.${GC.FIELD_GEO_FENCING_ZONE_CITIES}`,
  }, fields);
};
